import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  Message,
  Conversation,
  RecipeContext,
  ConversationState,
} from '../types/assistant';

interface ConversationStore extends ConversationState {
  // Actions
  createConversation: (context?: RecipeContext) => string;
  addMessage: (conversationId: string, messageData: Omit<Message, 'id' | 'timestamp'>) => void;
  setActiveConversation: (conversationId?: string) => void;
  setTyping: (isTyping: boolean) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error?: string) => void;
  clearError: () => void;
  deleteConversation: (conversationId: string) => void;
  clearAllConversations: () => void;
  updateConversationContext: (conversationId: string, context: RecipeContext) => void;
  requestImageGenerationForRecipe: (recipeId: string) => void;
  clearPendingImageGeneration: () => void;
  
  // Getters
  getActiveConversation: () => Conversation | undefined;
  getConversationsByRecipe: (recipeId: string) => Conversation[];
  getRecentConversations: (limit?: number) => Conversation[];
  getTotalMessageCount: () => number;
}

export const useConversationStore = create<ConversationStore>()(
  persist(
    (set, get) => ({
  // State
  conversations: {},
  activeConversationId: undefined,
  lastActivity: undefined,
  isTyping: false,
  isLoading: false,
  error: undefined,
  pendingImageGenerationForRecipeId: null,

  // Actions
  requestImageGenerationForRecipe: (recipeId: string) => {
    const activeId = get().activeConversationId || get().createConversation();
    set({ pendingImageGenerationForRecipeId: recipeId, activeConversationId: activeId });
  },

  clearPendingImageGeneration: () => {
    set({ pendingImageGenerationForRecipeId: null });
  },

  createConversation: (context?: RecipeContext) => {
    const conversationId = `conv-${Date.now()}`;
    const now = new Date();
    
    const newConversation: Conversation = {
      id: conversationId,
      title: context?.recipe.title ? `Chat about ${context.recipe.title}` : 'General Cooking Chat',
      messages: [],
      createdAt: now,
      updatedAt: now,
      recipeId: context?.recipe.id,
    };

    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversationId]: newConversation,
      },
      activeConversationId: conversationId,
      lastActivity: now
    }));

    return conversationId;
  },

  addMessage: (conversationId: string, messageData: Omit<Message, 'id' | 'timestamp'>) => {
    set((state) => {
      const conversation = state.conversations[conversationId];
      if (!conversation) {
        return state;
      }

      const newMessage: Message = {
        ...messageData,
        id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date(),
      };

      const updatedConversation = {
        ...conversation,
        messages: [...conversation.messages, newMessage],
        updatedAt: new Date(),
      };

      return {
        ...state,
        conversations: {
          ...state.conversations,
          [conversationId]: updatedConversation,
        },
        lastActivity: new Date()
      };
    });

  },

  setActiveConversation: (conversationId?: string) => {
    set((state) => {
      // Create a default conversation if none exists and conversationId is provided
      if (conversationId && !state.conversations[conversationId]) {
        return state;
      }

      return {
        ...state,
        activeConversationId: conversationId,
        lastActivity: new Date()
      };
    });
  },

  setTyping: (isTyping: boolean) => {
    set((state) => ({ ...state, isTyping }));
  },

  setLoading: (isLoading: boolean) => {
    set((state) => ({ ...state, isLoading }));
  },

  setError: (error?: string) => {
    set((state) => ({ ...state, error }));
  },

  clearError: () => {
    set((state) => ({ ...state, error: undefined }));
  },

  deleteConversation: (conversationId: string) => {
    set((state) => {
      const { ...remainingConversations } = state.conversations;
      
      const newActiveId = state.activeConversationId === conversationId
        ? Object.keys(remainingConversations)[0] || undefined
        : state.activeConversationId;

      return {
        ...state,
        conversations: remainingConversations,
        activeConversationId: newActiveId,
        lastActivity: new Date()
      };
    });
  },

  clearAllConversations: () => {
    set({
      conversations: {},
      activeConversationId: undefined,
      lastActivity: new Date(),
      isTyping: false,
      isLoading: false,
      error: undefined,
    });
  },

  updateConversationContext: (conversationId: string, context: RecipeContext) => {
    set((state) => {
      const conversation = state.conversations[conversationId];
      if (!conversation) {
        return state;
      }

      const updatedConversation = {
        ...conversation,
        recipeId: context.recipe.id,
        title: `Chat about ${context.recipe.title}`,
        updatedAt: new Date(),
      };

      return {
        ...state,
        conversations: {
          ...state.conversations,
          [conversationId]: updatedConversation,
        },
        lastActivity: new Date()
      };
    });
  },

  // Getters
  getActiveConversation: () => {
    const state = get();
    return state.activeConversationId
      ? state.conversations[state.activeConversationId]
      : undefined;
  },

  getConversationsByRecipe: (recipeId: string): Conversation[] => {
    const state = get();
    return Object.values(state.conversations)
      .filter((conversation: Conversation) => conversation.recipeId === recipeId)
      .sort((a: Conversation, b: Conversation) => b.updatedAt.getTime() - a.updatedAt.getTime());
  },

  getRecentConversations: (limit: number = 10): Conversation[] => {
    const state = get();
    return Object.values(state.conversations)
      .sort((a: Conversation, b: Conversation) => b.updatedAt.getTime() - a.updatedAt.getTime())
      .slice(0, limit);
  },

  getTotalMessageCount: (): number => {
    const state = get();
    return Object.values(state.conversations)
      .reduce((total: number, conversation: Conversation) => total + conversation.messages.length, 0);
  },
    }),
    {
      name: 'conversation-store',
      // Only persist essential data, let temporary states reset
      partialize: (state) => ({
        conversations: state.conversations,
        activeConversationId: state.activeConversationId,
        lastActivity: state.lastActivity,
        // Reset temporary states on load
        isTyping: false,
        isLoading: false,
        error: undefined,
        pendingImageGenerationForRecipeId: null,
      }),
      // Handle Date object serialization automatically with storage transforms
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          try {
            const parsed = JSON.parse(str);
            // Restore Date objects from ISO strings
            if (parsed.state && parsed.state.conversations) {
              Object.values(parsed.state.conversations as any).forEach((conv: any) => {
                if (conv.createdAt) conv.createdAt = new Date(conv.createdAt);
                if (conv.updatedAt) conv.updatedAt = new Date(conv.updatedAt);
                if (conv.messages) {
                  conv.messages.forEach((msg: any) => {
                    if (msg.timestamp) msg.timestamp = new Date(msg.timestamp);
                  });
                }
              });
            }
            if (parsed.state && parsed.state.lastActivity) {
              parsed.state.lastActivity = new Date(parsed.state.lastActivity);
            }
            return parsed;
          } catch {
            return null;
          }
        },
        setItem: (name, value) => {
          try {
            // Sanitize the data before storing to remove large binary content
            const sanitizedValue = sanitizeConversationForStorage(value);
            const jsonString = JSON.stringify(sanitizedValue);
            localStorage.setItem(name, jsonString);
          } catch (e) {
            // If quota exceeded, clear old conversations and try again
            if (e instanceof Error && e.name === 'QuotaExceededError') {
              try {
                // Use more aggressive cleanup
                cleanupLocalStorageConversations(3); // Keep only 3 most recent conversations

                // Try to save sanitized data again
                const sanitizedValue = sanitizeConversationForStorage(value);
                localStorage.setItem(name, JSON.stringify(sanitizedValue));
              } catch {
                // Last resort: clear current conversation store entirely
                try {
                  localStorage.removeItem(name);
                } catch {
                }
              }
            }
          }
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
    }
  )
);

// Utility function to filter out large binary data from conversations before localStorage persistence
function sanitizeConversationForStorage(value: any): any {
  if (!value || typeof value !== 'object') return value;
  
  // Deep clone to avoid mutating original data
  const sanitized = JSON.parse(JSON.stringify(value));
  
  // If this is the conversation store state
  if (sanitized.state && sanitized.state.conversations) {
    Object.values(sanitized.state.conversations).forEach((conversation: any) => {
      if (conversation.messages) {
        conversation.messages.forEach((message: any) => {
          if (message.metadata) {
            // Much more aggressive filtering - only keep essential metadata
            const filteredMetadata: any = {};
            
            Object.keys(message.metadata).forEach(key => {
              const value = message.metadata[key];
              
              // Skip ALL large content including recipe data
              if (typeof value === 'string') {
                // Skip base64 images completely
                if (value.startsWith('data:image/') ||
                    (value.length > 500 && /^[A-Za-z0-9+/=]+$/.test(value))) {
                  return; // Skip this field
                }
                // Much more aggressive text size limit (1KB instead of 10KB)
                if (value.length > 1000) {
                  return; // Skip entirely instead of truncating
                }
              }
              
              // Skip objects that might contain large recipe data
              if (typeof value === 'object' && value !== null) {
                const objSize = JSON.stringify(value).length;
                if (objSize > 2000) {
                  return; // Skip large objects entirely
                }
              }
              
              // Only keep small, essential metadata
              filteredMetadata[key] = value;
            });
            
            message.metadata = filteredMetadata;
          }
          
          // Also limit message content itself if it's too large
          if (message.content && typeof message.content === 'string' && message.content.length > 5000) {
            message.content = message.content.substring(0, 5000) + '... [truncated for storage]';
          }
          
          if (message.text && typeof message.text === 'string' && message.text.length > 5000) {
            message.text = message.text.substring(0, 5000) + '... [truncated for storage]';
          }
        });
      }
    });
  }
  
  return sanitized;
}

// Monitor localStorage usage
function getLocalStorageUsage(): { used: number; total: number; percentage: number } {
  let total = 0;
  for (const key in localStorage) {
    if (Object.prototype.hasOwnProperty.call(localStorage, key)) {
      total += localStorage[key].length + key.length;
    }
  }
  
  // Most browsers have ~5-10MB localStorage limit
  const estimatedLimit = 5 * 1024 * 1024; // 5MB conservative estimate
  
  return {
    used: total,
    total: estimatedLimit,
    percentage: Math.round((total / estimatedLimit) * 100)
  };
}

// Clean up old conversations from localStorage more aggressively when needed
function cleanupLocalStorageConversations(keepRecent: number = 5): void {
  try {
    const keys = Object.keys(localStorage).filter(key => key.startsWith('conversation-'));
    
    // Sort by timestamp (newer first) and keep only recent ones
    const sortedKeys = keys.sort((a, b) => {
      const timeA = parseInt(a.split('_')[1] || '0');
      const timeB = parseInt(b.split('_')[1] || '0');
      return timeB - timeA;
    });
    
    // Remove old conversations beyond the keep limit
    const toRemove = sortedKeys.slice(keepRecent);
    toRemove.forEach(key => {
      localStorage.removeItem(key);
    });
  } catch {
  }
}

// Cleanup utility for managing storage size
export const cleanupOldConversations = () => {
  const state = useConversationStore.getState();
  const conversations = Object.values(state.conversations);
  
  // Keep only last 50 conversations
  const maxConversations = 50;
  
  if (conversations.length > maxConversations) {
    const sortedConversations = conversations.sort(
      (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime()
    );
    
    sortedConversations.slice(0, maxConversations);
    const toDelete = sortedConversations.slice(maxConversations);
    
    toDelete.forEach(conversation => {
      state.deleteConversation(conversation.id);
    });
    
  }
};

// Auto-cleanup on store creation
if (typeof window !== 'undefined') {
  // Run cleanup on app start
  setTimeout(cleanupOldConversations, 1000);
  
  // Run cleanup periodically (every 30 minutes)
  setInterval(cleanupOldConversations, 30 * 60 * 1000);
  
  // Monitor localStorage usage every 5 minutes and warn if getting full
  setInterval(() => {
    const usage = getLocalStorageUsage();
    if (usage.percentage > 80) {
      if (usage.percentage > 90) {
        cleanupLocalStorageConversations(2); // Keep only 2 most recent when very full
      }
    }
  }, 5 * 60 * 1000);
}

// Export utility functions for external use
export { getLocalStorageUsage, cleanupLocalStorageConversations };
export default useConversationStore;